import type React from "react";
import {useRef} from "react";
import {useAuthState} from "react-firebase-hooks/auth";

import {useTexture} from "@react-three/drei";
import {useFrame} from "@react-three/fiber";
import {defaultLevelConfig} from "@repo/shared";
import {
  type BufferGeometry,
  Color,
  type InstancedMesh,
  LinearSRGBColorSpace,
  type Material,
  Object3D,
} from "three";

import {useLazyCallbackUser} from "~/api/hooks/useUser";
import {useGame} from "~/contexts/game-manager/GameManagerProvider";
import {auth} from "~/firebase/firebase";
import NormalHueShiftMaterial from "~/three/effects/trail/NormalHueShiftMaterial";

const dummy = new Object3D();
const zIndex = 60;

const WHITE = new Color("white");
const Players: React.FC = () => {
  const [user] = useAuthState(auth);
  const {gameManager} = useGame();
  const particleTexture = useTexture("/images/player-3.png");

  const lookupUser = useLazyCallbackUser();
  const reusableColor = useRef(new Color());

  useFrame(() => {
    const players = gameManager
      ? gameManager.getAllPlayers().filter((p) => !p.player.isDead)
      : [];

    players.sort((a) => (a.player.ownerId === user?.uid ? 1 : -1));

    if (circleMeshRef.current) {
      dummy.position.set(0, 0, 0);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();

      // reset unused
      for (
        let i = players.length;
        i < defaultLevelConfig.maxAmountPlayers;
        i++
      ) {
        circleMeshRef.current!.setMatrixAt(i, dummy.matrix);
        circleMeshRef.current!.setColorAt(i, WHITE);
      }

      players.forEach((player, i) => {
        const position = player.smoothPlayerPosition;
        dummy.position.set(position.x, position.y, zIndex);

        dummy.updateMatrix();
        circleMeshRef.current!.setMatrixAt(i, dummy.matrix);

        reusableColor.current.setStyle(
          lookupUser({id: player.player.ownerId}).color,
          LinearSRGBColorSpace,
        );
        reusableColor.current.offsetHSL(player.player.instanceId / 3, 0, 0);
        if (i < players.length - 1) reusableColor.current.lerp(WHITE, 0.5);
        circleMeshRef.current!.setColorAt(i, reusableColor.current);
      });
      circleMeshRef.current.count = players.length;
      circleMeshRef.current.instanceMatrix.needsUpdate = true;
      circleMeshRef.current.instanceColor!.needsUpdate = true;
      circleMeshRef.current.computeBoundingSphere();
    }
  });

  const circleMeshRef =
    useRef<InstancedMesh<BufferGeometry, Material | Material[]>>(null);

  return (
    <>
      <instancedMesh
        ref={circleMeshRef}
        args={[undefined, undefined, defaultLevelConfig.maxAmountPlayers]}
        frustumCulled={false}
      >
        <circleGeometry args={[defaultLevelConfig.playerRadius, 64]} />
        <NormalHueShiftMaterial map={particleTexture} />
      </instancedMesh>
    </>
  );
};

export default Players;
